import { useState } from "react";
import { Link } from "react-router-dom";
import { ImageOff, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import EmptyState from "../components/ui/EmptyState";
import { formatMoney } from "../utils/money";

export default function Cart() {
  const { items, incQty, decQty, removeItem, clear } = useCart();
  const { showPrices } = useSettings();
  // "Clear Cart" used to wipe every line on a single click with no way back —
  // ask once, inline, instead of a full modal for a one-line confirmation.
  const [confirmingClear, setConfirmingClear] = useState(false);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-xl font-semibold text-neutral-900 mb-6">Cart</h1>
        <EmptyState title="Your cart is empty." note="Browse the shop to add products." />
        <div className="text-center">
          <Link to="/shop" className="text-sm text-neutral-600 underline">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  // One subtotal per currency — summing across different currencies would be meaningless.
  const subtotals = items.reduce((acc, i) => {
    const cur = i.currency || "USD";
    acc[cur] = (acc[cur] || 0) + (i.price || 0) * i.qty;
    return acc;
  }, {});

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-xl font-semibold text-neutral-900 mb-6">Cart</h1>

      <div className="space-y-3">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-3"
          >
            <div className="w-16 h-16 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden shrink-0">
              {item.image ? (
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              ) : (
                <ImageOff className="text-neutral-300" size={20} />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-900 truncate">{item.name}</p>
              {item.unit_code && <p className="text-xs text-neutral-400">{item.unit_code}</p>}
              {showPrices && (
                <p className="text-sm text-neutral-500">{formatMoney(item.price, item.currency)} each</p>
              )}
            </div>

            <div className="flex items-center border border-neutral-300 rounded-lg">
              <button onClick={() => decQty(item.id)} className="p-3" aria-label="Decrease quantity">
                <Minus size={14} />
              </button>
              <span className="w-8 text-center text-sm">{item.qty}</span>
              <button onClick={() => incQty(item.id)} className="p-3" aria-label="Increase quantity">
                <Plus size={14} />
              </button>
            </div>

            <button
              onClick={() => removeItem(item.id)}
              className="p-3 text-neutral-400 hover:text-red-600"
              aria-label="Remove item"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-xl border border-neutral-200 bg-white p-4">
        {showPrices ? (
          <div className="space-y-1">
            {Object.entries(subtotals).map(([cur, amount]) => (
              <div key={cur} className="flex items-center justify-between text-sm font-medium">
                <span>Subtotal</span>
                <span>{formatMoney(amount, cur)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-neutral-500 text-center">
            Price will be provided by the business.
          </p>
        )}
      </div>

      <div className="flex items-center justify-between mt-4 gap-3">
        {confirmingClear ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-neutral-600">Clear all {items.length} item{items.length === 1 ? "" : "s"}?</span>
            <button
              onClick={() => { clear(); setConfirmingClear(false); }}
              className="font-semibold text-red-600 hover:text-red-700"
            >
              Yes, clear
            </button>
            <button onClick={() => setConfirmingClear(false)} className="text-neutral-500 hover:text-neutral-700">
              Cancel
            </button>
          </div>
        ) : (
          <button onClick={() => setConfirmingClear(true)} className="text-sm text-neutral-500 hover:text-red-600">
            Clear Cart
          </button>
        )}
        <div className="flex items-center gap-3">
          <Link to="/shop" className="text-sm text-neutral-600 underline">
            Continue Shopping
          </Link>
          <Link
            to="/quote"
            className="bg-neutral-900 text-white text-sm font-medium rounded-lg px-4 py-2.5 hover:bg-neutral-800"
          >
            Request a Quote
          </Link>
        </div>
      </div>
    </div>
  );
}
